/* Order Details status banner — mirrors the merchant panel's StatusBanner.
   Status-colored top strip, AWB + copy, ref/channel/courier line, a row of tag chips, and a
   right-aligned status chip + OTP/COD chips. Presentational; values come from the admin order. */
import React from 'react'
import { Box, Chip, Typography, Tooltip } from '@mui/material'
import CopyIcon from '../../../common/CopyIcon'
import { formatDate } from '../../../common/util'
import { T } from './detailKit'

function statusConfig(status) {
  const label = status?.replaceAll('_', ' ') || ''
  if (['DELIVERED', 'RTO_DELIVERED'].includes(status)) return { color: T.SUCCESS, bg: T.SUCCESS_BG, label }
  if (['FAILED_DELIVERY', 'SHIPMENT_HELD', 'RTO', 'RTO_INTRANSIT', 'CANCELLED_ORDER'].includes(status)) return { color: T.ERROR, bg: T.ERROR_BG, label }
  if (['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'OUT_FOR_PICKUP'].includes(status)) return { color: T.WARNING, bg: T.WARNING_BG, label }
  return { color: T.PRIMARY, bg: T.INFO_BG, label }
}

const chipSx = { fontFamily: T.FONT, fontWeight: 600, fontSize: 11 }
const tagSx = { ...chipSx, borderColor: T.BORDER, color: T.NAVY }

export default function StatusBanner({ order, courierAccountCode, serviceType, paymentMode, action }) {
  if (!order) return null

  const awb = order.awb_number || '—'
  const channel = order.channel_name || '-'
  const status = order.orderStatus || ''
  const cfg = statusConfig(status)
  const orderType = order.order_type || ''
  const courierParent = order.courierDetail?.parent?.includes('Blended')
    ? order.actualCourierProviderName
    : order.courierDetail?.parent || ''

  const netra = order.netra_address
  const netraScore = netra?.score != null && !Number.isNaN(Number(netra.score)) ? Math.round(Number(netra.score)) : null
  const netraCfg = netra?.band === 'EXCELLENT' ? { color: T.SUCCESS, bg: T.SUCCESS_BG }
    : netra?.band === 'GOOD' ? { color: T.PRIMARY, bg: T.INFO_BG }
    : netra?.band === 'FAIR' ? { color: T.WARNING, bg: T.WARNING_BG }
    : netra?.band === 'POOR' ? { color: T.ERROR, bg: T.ERROR_BG } : null

  const isCod = paymentMode === 'COD'
  // KCI-impacted flag comes through orderTags (string array) — e.g. "KCI_IMPACTED".
  const kciImpacted = (order.orderTags || []).some((t) => String(t).toUpperCase().includes('KCI'))
  // Courier partner's verdict on the drop address, set at booking from the PCIS create response.
  // Generic across couriers (today only Amazon ATS returns one). true = verified, false = evaluated
  // but not verified, null/undefined = this courier gave no signal → no chip at all.
  const addressVerified = order.cpAddressVerified
  const addressVerifier = courierParent || 'The courier partner'

  return (
    <Box sx={{ borderRadius: '14px', border: `1px solid ${T.BORDER}`, bgcolor: T.WHITE, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ height: 4, bgcolor: cfg.color }} />
      <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        {/* Left — identity */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography sx={{ fontFamily: T.FONT, fontWeight: 700, fontSize: 20, color: T.NAVY, letterSpacing: '0.5px' }}>{awb}</Typography>
            <CopyIcon text={order.awb_number} />
          </Box>
          <Typography sx={{ fontFamily: T.FONT, fontSize: 12, color: T.SUBTLE }}>
            Reference Number: <Box component='span' sx={{ color: T.NAVY, fontWeight: 600 }}>{order.reference || '-'}</Box> &middot; {channel}
          </Typography>
          {order.shippingLabelId && (
            <Typography sx={{ fontFamily: T.FONT, fontSize: 12, color: T.SUBTLE }}>
              Shipping Label ID: <Box component='span' sx={{ color: T.NAVY, fontWeight: 600 }}>{order.shippingLabelId}</Box>
            </Typography>
          )}
          {courierParent && (
            <Typography sx={{ fontFamily: T.FONT, fontSize: 12, color: T.SUBTLE }}>
              {courierParent}
              {serviceType ? ` · ${serviceType}` : ''}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {kciImpacted && (
              <Tooltip title='This shipment was tagged by a KCI (Keep Customers Informed) event — its delay is attributed to a known external disruption.'>
                <Chip label='KCI Impacted' size='small' sx={{ ...chipSx, fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #E6510055' }} />
              </Tooltip>
            )}
            {addressVerified === true && (
              <Tooltip title={`Verified Address — ${addressVerifier} verified this drop address at booking (high delivery confidence).`}>
                <Chip label='Verified Address' size='small' sx={{ ...chipSx, fontWeight: 700, bgcolor: T.SUCCESS_BG, color: T.SUCCESS, border: `1px solid ${T.SUCCESS}40` }} />
              </Tooltip>
            )}
            {addressVerified === false && (
              <Tooltip title={`${addressVerifier} evaluated this drop address at booking and did not verify it. Also shown when the courier's verification service was unavailable.`}>
                <Chip label='Address Not Verified' size='small' variant='outlined' sx={{ ...tagSx, color: T.SUBTLE, borderColor: T.BORDER }} />
              </Tooltip>
            )}
            {order.merchantDetail?.name && <Chip label={order.merchantDetail.name} size='small' sx={{ ...tagSx, bgcolor: '#F5F3FF', color: '#6D28D9' }} />}
            {paymentMode && (
              <Chip label={paymentMode} size='small' sx={{ ...chipSx, bgcolor: isCod ? T.ERROR_BG : T.SUCCESS_BG, color: isCod ? T.ERROR : T.SUCCESS, border: `1px solid ${isCod ? T.ERROR : T.SUCCESS}40` }} />
            )}
            {orderType && <Chip label={orderType} size='small' variant='outlined' sx={tagSx} />}
            {order.allocationServiceType && (
              <Tooltip title="Service asked for this order, set at creation from the merchant's Service Types rules (Allocation Config → Service Types). PACE ranks couriers of this service type first.">
                <Chip label={`Service asked: ${order.allocationServiceType}`} size='small' sx={{ ...tagSx, bgcolor: T.INFO_BG, color: T.PRIMARY, borderColor: `${T.PRIMARY}40` }} />
              </Tooltip>
            )}
            {order.isPartialPrepaid && <Chip label='Partial Prepaid' size='small' sx={{ ...tagSx, bgcolor: T.WARNING_BG, color: T.WARNING, borderColor: `${T.WARNING}60` }} />}
            {courierAccountCode && (
              <Tooltip title='Courier Account Code'><Chip label={courierAccountCode} size='small' variant='outlined' sx={{ ...tagSx, color: T.PRIMARY, borderColor: T.PRIMARY }} /></Tooltip>
            )}
            {order.rto_awb && <Chip label={`RTO: ${order.rto_awb}`} size='small' sx={{ ...chipSx, bgcolor: T.ERROR_BG, color: T.ERROR }} />}
            {orderType === 'REVERSE SHIPMENT' && order.rvp_reason && (
              <Chip label={`RVP: ${order.rvp_reason}`} size='small' sx={{ ...tagSx, bgcolor: T.WARNING_BG, color: '#e65100', border: '1px solid #e6510040' }} />
            )}
            {order.edd && (
              <Tooltip title='Expected Delivery Date'><Chip label={`EDD ${formatDate(order.edd, true)}`} size='small' variant='outlined' sx={tagSx} /></Tooltip>
            )}
            {order.customEdd && (
              <Tooltip title='Custom EDD — revised delivery date set for this shipment (e.g. via a KCI event).'>
                <Chip label={`Custom EDD ${formatDate(order.customEdd, true)}`} size='small' sx={{ ...chipSx, bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #E6510055' }} />
              </Tooltip>
            )}
            {order.proshipDeliveryTat != null && (
              <Tooltip title='Delivery TAT'><Chip label={`TAT ${order.proshipDeliveryTat}d`} size='small' variant='outlined' sx={tagSx} /></Tooltip>
            )}
            {netraScore != null && netraCfg && (
              <Chip label={`Addr. Quality: ${netraScore}`} size='small' sx={{ ...chipSx, bgcolor: netraCfg.bg, color: netraCfg.color, border: `1px solid ${netraCfg.color}40` }} />
            )}
          </Box>
        </Box>

        {/* Right — status + actions */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Chip label={cfg.label} sx={{ fontFamily: T.FONT, fontWeight: 700, fontSize: 12, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}40`, px: 1 }} />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(order.otpVerifiedDelivery || order.deliveryOtpSent) && <Chip label='OTP Delivery' size='small' sx={{ ...chipSx, bgcolor: T.SUCCESS_BG, color: T.SUCCESS }} />}
            {order.otpVerifiedCancellation && <Chip label='OTP Cancelled' size='small' sx={{ ...chipSx, bgcolor: T.ERROR_BG, color: T.ERROR }} />}
            {paymentMode === 'COD' && (
              <Chip label={order.merchantRemittanceStatus ? 'COD Remitted' : 'COD Pending'} size='small' variant='outlined'
                sx={{ ...chipSx, color: order.merchantRemittanceStatus ? T.SUCCESS : T.WARNING, borderColor: order.merchantRemittanceStatus ? T.SUCCESS : T.WARNING }} />
            )}
          </Box>
          {action}
        </Box>
      </Box>
    </Box>
  )
}
